'use client';

import React, { useCallback, useState } from 'react';
import { useParams } from 'next/navigation';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import {
  useGetCompanyUsersQuery,
  useUpdateCompanyUserMutation,
  useGetCompanyUserQuery,
  useGetManagerReportsQuery,
  useUpdateManagerReportsMutation,
} from 'src/api/redux';
import {
  Dialog,
  PulseButton,
  Typography,
  CircularProgress,
} from 'src/components/PulseUI';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { useSettings } from 'src/components/Settings/hooks';
import { useUserDeactivationRails } from 'src/hooks/authorization';
import { DEACTIVATE_DIALOG_COPY } from './constants';
interface ApiError {
  data: {
    error_message?: string;
  };
  status?: number;
}

interface DeactivateDialogProps {
  open: boolean;
  onClose: () => void;
}

const DeactivateDialog: React.FC<DeactivateDialogProps> = ({
  open,
  onClose,
}): React.ReactElement => {
  const log = useLogging();
  const { setNotification } = useNotification();
  const { selectedUser, setSelectedUser } = useSettings();

  // Get IDs from route params
  const { companyId, userId } = useParams<{
    companyId: string;
    userId: string;
  }>();
  const userIdToUser = userId || (selectedUser?.id != null ? String(selectedUser.id) : undefined);
  // Get user data from Redux query
  const {
    data: currentUser,
    refetch: mutateUser,
    isLoading: isUserLoading,
  } = useGetCompanyUserQuery(
    { companyId, userId: userIdToUser },
    { skip: !companyId || !userIdToUser }
  );

  const { refetch: refetchUsers } = useGetCompanyUsersQuery(
    { companyId },
    { skip: !companyId }
  );

  const [updateCompanyUser] = useUpdateCompanyUserMutation();
  const [updateManagerReports] = useUpdateManagerReportsMutation();
  const {
    data: reports,
    isLoading: isReportsLoading,
    refetch: mutateReports,
  } = useGetManagerReportsQuery(
    { companyId, managerId: userIdToUser },
    { skip: !companyId || !userIdToUser }
  );
  const isActive = currentUser?.is_active;

  // Re-checked here as well as on the button: the dialog is driven by shared
  // context, so it can be opened without going through a rail-aware button.
  const {
    isBlocked,
    reason: blockedReason,
    isLoading: isRailsLoading,
  } = useUserDeactivationRails(currentUser);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleDeactivate = async (): Promise<void> => {
    if (isBlocked) {
      setNotification({
        title: 'Not allowed',
        message: blockedReason ?? DEACTIVATE_DIALOG_COPY.blockedTitle,
        type: 'error',
      });
      return;
    }

    if (!companyId || !userIdToUser) {
      setNotification({
        title: 'Error',
        message: 'Missing required user or company information',
        type: 'error',
      });
      return;
    }

    setIsLoading(true);
    try {
      // Clear all reports before activating/deactivating
      if (reports?.results?.length > 0) {
        await updateManagerReports({
          companyId,
          managerId: userIdToUser,
          direct_reports: [],
        }).unwrap();

        log.event(
          isActive
            ? 'Reports Cleared Before Deactivation'
            : 'Reports Cleared During Reactivation',
          {
            source: 'DeactivateDialog',
            clearedReports: reports.results.map((report) => report.id),
          }
        );
      }

      // Then activate/deactivate the user
      const newUser = await updateCompanyUser({
        companyId,
        userId: userIdToUser,
        is_active: !isActive,
      }).unwrap();

      if (newUser?.id?.toString() === userIdToUser) {
        refetchUsers();
        mutateUser(); // Refresh current user data
      }

      log.event(`User Activation`, {
        source: 'DeactivateDialog',
        user: currentUser,
        active: !isActive,
      });

      setNotification({
        title: 'Success',
        message: `User ${isActive ? 'deactivated' : 'activated'} successfully`,
        type: 'success',
      });

      onClose();
      mutateUser();
      mutateReports(); // Refresh reports data
    } catch (err) {
      const error = err as ApiError;
      log.exception(`User Activation Error`, {
        extra: {
          source: 'DeactivateDialog',
          error,
          active: !isActive,
        },
      });

      setNotification({
        title: 'Error',
        message:
          error.data?.error_message ||
          `Error ${isActive ? 'deactivating' : 'activating'} user`,
        type: 'error',
      });
    } finally {
      setSelectedUser(null);
      setIsLoading(false);
    }
  };

  const handleCancel = useCallback(() => {
    setSelectedUser(null);
    onClose();
  }, [onClose]);

  const userName = currentUser
    ? `${currentUser.first_name} ${currentUser.last_name}`
    : '';

  // Licenses follow activation state, so say so before the viewer commits.
  const licenseNote = isActive
    ? DEACTIVATE_DIALOG_COPY.licenseFreed
    : DEACTIVATE_DIALOG_COPY.licenseConsumed;

  return (
    <Dialog
      isOpen={open}
      onClose={onClose}
      size="xl"
      showCloseButton
      className="h-auto min-h-96 max-h-[80vh]"
    >
      <Dialog.Header>
        <Typography variant="h5" data-testid="deactivate-title">
          {isActive ? 'Deactivate' : 'Activate'} User
        </Typography>
      </Dialog.Header>

      <Dialog.Content>
        {isUserLoading || isReportsLoading || isRailsLoading ? (
          <div className="flex justify-center py-8">
            <CircularProgress size="sm" />
          </div>
        ) : isBlocked ? (
          <div
            className="py-6 px-4 text-center"
            data-testid="deactivate-blocked"
          >
            <div className="w-12 h-12 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
              <Icon
                icon={faExclamationTriangle}
                className="text-amber-600 text-xl"
              />
            </div>
            <Typography variant="body1" className="text-neutral-800">
              {DEACTIVATE_DIALOG_COPY.blockedTitle}
            </Typography>
            {blockedReason && (
              <p
                className="mt-1 text-sm text-amber-700"
                data-testid="deactivate-blocked-reason"
              >
                {blockedReason}
              </p>
            )}
          </div>
        ) : reports?.results?.length > 0 && isActive ? (
          <div className="p-4">
            <div className="w-12 h-12 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
              <Icon
                icon={faExclamationTriangle}
                className="text-amber-600 text-xl"
              />
            </div>
            <p className="text-base text-neutral-600 text-center mb-1">
              Are you sure you want to deactivate{' '}
              <span className="font-medium text-neutral-800">{userName}</span>?
            </p>
            <p
              className="text-sm text-neutral-600 text-center mb-1"
              data-testid="deactivate-license-note"
            >
              {licenseNote}
            </p>
            <p className="text-sm text-neutral-600 text-center mb-3">
              {DEACTIVATE_DIALOG_COPY.reportsWarning}
            </p>
            <div className="max-h-64 overflow-y-auto space-y-2 border border-neutral-200 rounded-lg p-3 bg-neutral-50">
              {reports?.results?.map((report) => (
                <div
                  key={report.id}
                  className="flex items-center p-3 bg-white rounded-md border border-neutral-100"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-blue-600">
                        {report.first_name[0]}
                        {report.last_name[0]}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-neutral-800">
                      {`${report.first_name} ${report.last_name}`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div
            className="py-6 px-4 text-center"
            aria-describedby="deactivate-description"
          >
            <div className="w-12 h-12 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
              <Icon
                icon={faExclamationTriangle}
                className="text-amber-600 text-xl"
              />
            </div>
            <Typography variant="body1" className="text-neutral-600">
              Are you sure you want to {isActive ? 'deactivate' : 'activate'}{' '}
              <span className="font-medium text-neutral-800">{userName}</span>?
            </Typography>
            <p
              className="mt-1 text-sm text-neutral-600"
              data-testid="deactivate-license-note"
            >
              {licenseNote}
            </p>
          </div>
        )}
      </Dialog.Content>
      <Dialog.Actions>
        <PulseButton
          variant="outline"
          color="neutral"
          onClick={handleCancel}
          data-testid="deactivate-cancel-button"
          aria-label="Cancel action"
          className="rounded-full w-full"
        >
          Cancel
        </PulseButton>
        <PulseButton
          variant="solid"
          color={isActive ? 'danger' : 'success'}
          onClick={handleDeactivate}
          loading={isLoading}
          disabled={isBlocked || isRailsLoading}
          data-testid="deactivate-button"
          aria-label={`${isActive ? 'Deactivate' : 'Activate'} user`}
          className="rounded-full w-full"
        >
          {isActive ? 'Deactivate User' : 'Activate User'}
        </PulseButton>
      </Dialog.Actions>
    </Dialog>
  );
};

export default DeactivateDialog;
